import * as Dialog from "@radix-ui/react-dialog";
import { addDays } from "date-fns";
import { AppWindow, ChevronDown, Columns2 } from "lucide-react";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_process_details } from "../../api";
import type { Process, ProcessSummary } from "../../api/types";
import { formatDuration } from "../../utils";
import { AppSelect } from "../AppSelect";
import Modal from "../Modal";
import DashboardDateRange from "../dashboard/DashboardDateRange";
import ProcessComparisonMap from "./ProcessComparisonMap";
import ProcessInstancePicker, { useProcessSourceIdentity } from "./ProcessInstancePicker";
import ProcessRunRow from "./ProcessRunRow";
import ProcessSearchList from "./ProcessSearchList";
import { appIconUri } from "./appIcon";
import { compareSideColor } from "./processCompareModel";
import {
  type ComparisonMode,
  type DurationChoice,
  type ProcessComparison,
  appGroups,
  comparisonPeriods,
  durationGroups,
  periodDates,
  periodFromDates,
  previousPeriodFor,
  runsInPeriod,
} from "./processComparison";
import {
  EMPTY_INSTANCE_SCOPE,
  buildInstanceScopeIndex,
  matchInstanceScope,
  scopeUserOptions,
} from "./processScopeModel";
import { runApps } from "./processSearch";
import type { ProcessGraphState } from "./useProcessGraph";

export default function ProcessCompareAction({
  state,
  processes = [],
  people = {},
}: {
  state: ProcessGraphState;
  processes?: ProcessSummary[];
  people?: Record<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const scopeId = useId();
  const sourceProcess = state.comparison?.a.process ?? state.baseProcess ?? state.process;
  const scopedRuns = useMemo(() => {
    if (state.comparison?.mode === "processes") return state.comparison.a.runs;
    // Simplification chooses routes to draw, not the population to compare. Explicit filters
    // and manual case exclusions still apply, even when the graph temporarily reveals all paths.
    const ids = new Set(
      matchInstanceScope(buildInstanceScopeIndex(sourceProcess.instances), {
        ...state.appliedScope,
        commonPaths: false,
      }).includedIds,
    );
    return sourceProcess.instances.filter(run => ids.has(run.id));
  }, [sourceProcess.instances, state.appliedScope, state.comparison]);
  const [mode, setMode] = useState<ComparisonMode>("duration");
  const [duration, setDuration] = useState<DurationChoice>("longerShorter");
  const [useAll, setUseAll] = useState(false);
  const [period, setPeriod] = useState(comparisonPeriods);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [fullProcess, setFullProcess] = useState<Process | null>(null);
  const [otherSlug, setOtherSlug] = useState("");
  const [otherProcess, setOtherProcess] = useState<Process | null>(null);
  const [fullError, setFullError] = useState(false);
  const [otherError, setOtherError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const trigger = useRef<HTMLButtonElement>(null);
  const needsFullProcess = useAll || mode === "processes";
  // biome-ignore lint/correctness/useExhaustiveDependencies: Retry intentionally repeats the request.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFullProcess(null);
    setFullError(false);
    get_process_details(sourceProcess.slug)
      .then(process => {
        if (!cancelled) setFullProcess(process);
      })
      .catch(() => {
        if (!cancelled) setFullError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, sourceProcess.slug, attempt]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Retry intentionally repeats the request.
  useEffect(() => {
    if (!open || mode !== "processes" || !otherSlug) return;
    let cancelled = false;
    setOtherProcess(null);
    setOtherError(false);
    get_process_details(otherSlug)
      .then(process => {
        if (!cancelled) setOtherProcess(process);
      })
      .catch(() => {
        if (!cancelled) setOtherError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, otherSlug, attempt]);
  const process = needsFullProcess ? (fullProcess ?? sourceProcess) : sourceProcess;
  const base = useAll ? process.instances : scopedRuns;
  const durationSelection = useMemo(() => durationGroups(base, duration), [base, duration]);
  const otherRuns = useMemo(() => {
    if (!otherProcess) return [];
    if (useAll) return otherProcess.instances;
    const { userIds, noUsers, from, to } = state.appliedScope;
    const ids = new Set(
      matchInstanceScope(buildInstanceScopeIndex(otherProcess.instances), {
        ...EMPTY_INSTANCE_SCOPE,
        commonPaths: false,
        userIds,
        noUsers,
        from,
        to,
      }).includedIds,
    );
    return otherProcess.instances.filter(run => ids.has(run.id));
  }, [otherProcess, useAll, state.appliedScope]);
  const allRunsSelected = useMemo(() => {
    if (!fullProcess || scopedRuns.length !== fullProcess.instances.length) return false;
    const selectedIds = new Set(scopedRuns.map(run => run.id));
    return fullProcess.instances.every(run => selectedIds.has(run.id));
  }, [fullProcess, scopedRuns]);
  const identity = useProcessSourceIdentity(process);
  const users = useMemo(() => scopeUserOptions(base), [base]);
  const error = (needsFullProcess && fullError) || (mode === "processes" && otherError);
  const appCaseCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const run of base) {
      // runApps is distinct per case: repeated steps in an app still count as one case.
      for (const app of runApps(run)) counts.set(app, (counts.get(app) ?? 0) + 1);
    }
    return counts;
  }, [base]);
  const apps = [...appCaseCounts.keys()].sort();
  const pending =
    (needsFullProcess && !fullProcess) ||
    (mode === "processes" && !!otherSlug && otherProcess?.slug !== otherSlug);
  const comparison = useMemo((): ProcessComparison | null => {
    const make = (
      a: typeof base,
      b: typeof base,
      aLabel: string,
      bLabel: string,
      extra: Partial<ProcessComparison> = {},
    ): ProcessComparison | null =>
      a.length && b.length
        ? {
            mode,
            useAll,
            a: { label: aLabel, process, runs: a },
            b: { label: bLabel, process, runs: b },
            ...extra,
          }
        : null;
    if (mode === "duration") {
      const groups = durationSelection;
      if (!groups) return null;
      return make(
        groups.a,
        groups.b,
        t(duration === "shorterTypical" ? "processPage.shorterCases" : "processPage.longerCases"),
        t(duration === "longerShorter" ? "processPage.shorterCases" : "processPage.typicalCases"),
        { duration },
      );
    }
    if (mode === "period") {
      if (period.a.every((date, i) => date.getTime() === period.b[i].getTime())) return null;
      const label = (range: [Date, Date]) => {
        const formatter = new Intl.DateTimeFormat(i18n?.resolvedLanguage, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
        return `${formatter.format(range[0])} – ${formatter.format(addDays(range[1], -1))}`;
      };
      return make(
        runsInPeriod(base, period.a),
        runsInPeriod(base, period.b),
        label(period.a),
        label(period.b),
        { period },
      );
    }
    if (mode === "processes")
      return otherProcess?.slug === otherSlug
        ? make(base, otherRuns, process.name, otherProcess.name, {
            b: {
              label: otherProcess.name,
              process: otherProcess,
              runs: otherRuns,
            },
          })
        : null;
    if (!aId || !bId || aId === bId) return null;
    if (mode === "people")
      return make(
        base.filter(run => run.source.userId === aId),
        base.filter(run => run.source.userId === bId),
        users.find(user => user.userId === aId)?.userName || t("user"),
        users.find(user => user.userId === bId)?.userName || t("user"),
      );
    if (mode === "apps") {
      const groups = appGroups(base, aId, bId);
      return make(groups.a, groups.b, aId, bId, { excluded: groups.excluded });
    }
    const left = base.find(run => run.id === aId);
    const right = base.find(run => run.id === bId);
    return left && right
      ? make([left], [right], identity(left.source).fullLabel, identity(right.source).fullLabel)
      : null;
  }, [
    mode,
    base,
    duration,
    durationSelection,
    aId,
    bId,
    process,
    otherProcess,
    otherRuns,
    otherSlug,
    identity,
    users,
    t,
    useAll,
    period,
    i18n?.resolvedLanguage,
  ]);
  const showPreview = !!comparison && !pending && !error;
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  const changeMode = (next: ComparisonMode) => {
    setMode(next);
    setAId("");
    setBId("");
  };
  return (
    <>
      <button
        ref={trigger}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          const current = state.comparison;
          setMode(current?.mode ?? "duration");
          setDuration(current?.duration ?? "longerShorter");
          setUseAll(current?.useAll ?? false);
          setPeriod(current?.period ?? comparisonPeriods());
          setAId(
            current?.mode === "people"
              ? current.a.runs[0].source.userId
              : current?.mode === "apps"
                ? current.a.label
                : current?.mode === "runs"
                  ? current.a.runs[0].id
                  : "",
          );
          setBId(
            current?.mode === "people"
              ? current.b.runs[0].source.userId
              : current?.mode === "apps"
                ? current.b.label
                : current?.mode === "runs"
                  ? current.b.runs[0].id
                  : "",
          );
          setOtherSlug(current?.mode === "processes" ? current.b.process.slug : "");
          setOtherProcess(null);
          setFullProcess(null);
          setOpen(true);
        }}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-outline bg-surface px-3 text-xs font-medium text-text-mid transition-colors hover:bg-surface-strong hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <Columns2 size={14} aria-hidden="true" />
        {t(state.comparison ? "processPage.change" : "compare")}
      </button>
      <Modal open={open} onOpenChange={next => !next && close()} size={showPreview ? "wide" : "lg"}>
        <Dialog.Title className="pr-10 text-lg font-semibold">
          {t("processPage.compareTitle", { name: sourceProcess.name })}
        </Dialog.Title>
        <Dialog.Description className="mt-2 text-sm text-text-mid">
          {t("processPage.compareIntro")}
        </Dialog.Description>
        <div
          className={`mt-4 grid grid-cols-1 gap-6 ${showPreview ? "lg:grid-cols-[20rem_minmax(0,1fr)]" : ""}`}
        >
          <div className="min-w-0 space-y-4">
            <div className="space-y-2 border-b border-outline/60 pb-4">
              {mode !== "processes" && allRunsSelected ? (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-text-mid">{t("processPage.scope")}</div>
                  <div className="rounded-lg bg-muted-surface px-3 py-2 text-sm text-text">
                    {t("processPage.allRunsCount", { count: scopedRuns.length })}
                  </div>
                </div>
              ) : (
                <fieldset>
                  <legend className="mb-2 text-xs font-medium text-text-mid">
                    {t("processPage.scope")}
                  </legend>
                  <div className="flex rounded-full border border-outline bg-muted-surface p-1">
                    {([false, true] as const).map(all => (
                      <label key={String(all)} className="min-w-0 flex-1 cursor-pointer">
                        <input
                          type="radio"
                          name={scopeId}
                          checked={useAll === all}
                          className="peer sr-only"
                          onChange={() => {
                            setUseAll(all);
                            setAId("");
                            setBId("");
                          }}
                        />
                        <span className="flex min-h-8 items-center justify-center rounded-full px-2 py-1 text-center text-xs font-medium text-text-mid transition-colors hover:text-text peer-checked:bg-surface peer-checked:text-text peer-checked:shadow-sm peer-checked:ring-1 peer-checked:ring-outline peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-primary">
                          {all
                            ? mode === "processes"
                              ? t("processPage.allProcessRuns")
                              : fullProcess
                                ? t("processPage.allRunsCount", {
                                    count: fullProcess.instances.length,
                                  })
                                : t("processPage.allAvailableRuns")
                            : t("processPage.currentScope", { count: scopedRuns.length })}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
              {(mode === "processes" || !allRunsSelected) && (
                <p className="text-xs text-text-mid">
                  {t(
                    mode === "processes"
                      ? useAll
                        ? "processPage.processScope"
                        : "askPanel.compare_filtered_scope"
                      : useAll
                        ? "processPage.allScopeHelp"
                        : "processPage.currentScopeHelp",
                  )}
                </p>
              )}
              {state.simplified && (
                <p className="text-xs leading-relaxed text-text-mid">
                  {t("processPage.compareSimplifiedHelp")}
                </p>
              )}
            </div>
            <h2 className="text-sm font-semibold">{t("processPage.compareQuestion")}</h2>
            <fieldset
              aria-label={t("processPage.compareQuestion")}
              className="grid grid-cols-2 gap-2 sm:grid-cols-3"
            >
              {(["duration", "people", "period", "runs", "apps", "processes"] as const).map(
                value => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={mode === value}
                    onClick={() => changeMode(value)}
                    className={`rounded-lg border p-3 text-left text-sm font-medium focus-visible:ring-2 focus-visible:ring-primary ${mode === value ? "border-info bg-info/10 text-info" : "border-outline hover:bg-muted-surface"}`}
                  >
                    {t(`processPage.compare_${value}`)}
                  </button>
                ),
              )}
            </fieldset>
            {mode === "period" && (
              <>
                {!useAll && !allRunsSelected && (
                  <p className="text-xs text-text-mid">{t("processPage.periodScope")}</p>
                )}
                <div className="space-y-3">
                  {(["a", "b"] as const).map(side => (
                    <div key={side} className="space-y-1">
                      <div className="text-sm">
                        {t(side === "a" ? "processPage.periodA" : "processPage.periodB")}
                      </div>
                      <DashboardDateRange
                        {...periodDates(period[side])}
                        previousPeriod={previousPeriodFor(period[side === "a" ? "b" : "a"])}
                        label={t(side === "a" ? "processPage.periodA" : "processPage.periodB")}
                        modal
                        allowAllTime={false}
                        onChange={(from, to) =>
                          setPeriod(current => ({
                            ...current,
                            [side]: periodFromDates(from, to),
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-text-dim">{t("processPage.periodHelp")}</p>
              </>
            )}
            {mode === "duration" && (
              <>
                <div className="space-y-2">
                  {(["longerShorter", "longerTypical", "shorterTypical"] as const).map(value => (
                    <label key={value} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="duration-comparison"
                        value={value}
                        checked={duration === value}
                        onChange={() => setDuration(value)}
                      />
                      {t(`processPage.${value}`)}
                    </label>
                  ))}
                </div>
                <p className="text-xs leading-relaxed text-text-dim">
                  {t("processPage.durationGroupsHelp")}
                </p>
                {base.length < 3 && (
                  <p className="text-xs text-text-mid">{t("processPage.durationSmallScope")}</p>
                )}
              </>
            )}
            {(mode === "people" || mode === "apps") && (
              <div className="grid grid-cols-2 gap-3">
                {(["a", "b"] as const).map(side => {
                  const label = t(
                    mode === "people"
                      ? side === "a"
                        ? "processPage.personA"
                        : "processPage.personB"
                      : side === "a"
                        ? "processPage.appA"
                        : "processPage.appB",
                  );
                  return (
                    <div key={side} className="min-w-0 space-y-1 text-sm">
                      <span>{label}</span>
                      <AppSelect
                        label={label}
                        disabled={pending}
                        value={side === "a" ? aId : bId}
                        onChange={side === "a" ? setAId : setBId}
                        placeholder={t("processPage.choose")}
                        contentClassName="pd-theme z-[120]"
                        choices={(mode === "people"
                          ? users.map(user => ({
                              id: user.userId,
                              display: `${user.userName || t("user")} (${user.instanceCount})`,
                            }))
                          : apps.map(app => {
                              const icon = appIconUri(app);
                              return {
                                id: app,
                                display: `${app} (${appCaseCounts.get(app)})`,
                                icon: icon ? (
                                  <img
                                    src={icon}
                                    alt=""
                                    className="h-4 w-4 shrink-0 object-contain"
                                  />
                                ) : (
                                  <AppWindow
                                    aria-hidden="true"
                                    className="h-4 w-4 shrink-0 text-text-dim"
                                  />
                                ),
                              };
                            })
                        ).map(option => ({
                          ...option,
                          disabled: option.id === (side === "a" ? bId : aId),
                        }))}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {mode === "apps" && (
              <p className="text-xs text-text-mid">{t("processPage.appScope")}</p>
            )}
            {mode === "runs" && (
              <div className="space-y-3">
                {(["a", "b"] as const).map(side => {
                  const run = base.find(run => run.id === (side === "a" ? aId : bId));
                  return (
                    <div key={side}>
                      <span className="text-sm">
                        {t(side === "a" ? "compare_run_a" : "compare_run_b")}
                      </span>
                      <ProcessInstancePicker
                        modal
                        process={process}
                        options={base
                          .filter(run => run.id !== (side === "a" ? bId : aId))
                          .map(instance => ({
                            instance,
                            identity: identity(instance.source),
                          }))}
                        selectedInstanceId={side === "a" ? aId : bId}
                        onSelect={side === "a" ? setAId : setBId}
                        label={t(side === "a" ? "compare_run_a" : "compare_run_b")}
                      >
                        <button type="button" className="dropdown-trigger mt-1 w-full">
                          <span className="min-w-0 flex-1 truncate">
                            {run ? identity(run.source).fullLabel : t("compare_choose_run")}
                          </span>
                          <ChevronDown
                            aria-hidden="true"
                            className="h-4 w-4 shrink-0 text-text-dim"
                          />
                        </button>
                      </ProcessInstancePicker>
                    </div>
                  );
                })}
              </div>
            )}
            {mode === "processes" && (
              <div className="flex max-h-72 flex-col overflow-hidden rounded-lg border border-outline">
                <ProcessSearchList
                  processes={processes.filter(candidate => candidate.slug !== process.slug)}
                  people={people}
                  browse={false}
                  current={otherSlug}
                  onSelect={candidate => setOtherSlug(candidate.slug)}
                />
              </div>
            )}
            {error ? (
              <div role="alert" className="text-sm text-danger-strong">
                <p>{t("processPage.unavailable")}</p>
                <button
                  type="button"
                  onClick={() => setAttempt(value => value + 1)}
                  className="mt-2 underline"
                >
                  {t("retry")}
                </button>
              </div>
            ) : pending ? (
              <output className="text-sm text-text-mid">{t("loading_ellipsis")}</output>
            ) : !comparison ? (
              <p className="text-xs text-text-dim">
                {t(
                  mode === "duration" && base.length >= (duration === "longerShorter" ? 2 : 3)
                    ? "processPage.emptyDurationGroups"
                    : "processPage.noComparison",
                )}
              </p>
            ) : null}
          </div>
          {showPreview && comparison && (
            <div className="min-w-0">
              <div className="grid gap-3 sm:grid-cols-2">
                {(["a", "b"] as const).map(side => (
                  <ComparisonPreview
                    key={side}
                    side={side}
                    group={comparison[side]}
                    durationRange={
                      mode === "duration" ? durationSelection?.ranges[side] : undefined
                    }
                  />
                ))}
              </div>
              {!!comparison.excluded && (
                <p className="mt-3 text-xs text-text-mid">
                  {t("processPage.excludedApps", {
                    count: comparison.excluded,
                  })}
                </p>
              )}
            </div>
          )}
          <div
            className={`flex justify-end gap-2 border-t border-outline pt-4 ${showPreview ? "lg:col-span-2" : ""}`}
          >
            <button
              type="button"
              onClick={close}
              className="rounded-lg border border-outline px-3 py-2 text-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="button"
              disabled={!comparison || pending || error}
              onClick={() => {
                if (!comparison || pending || error) return;
                state.startComparison(comparison);
                close();
              }}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-on-accent disabled:opacity-40"
            >
              {t("compare")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function ComparisonPreview({
  side,
  group,
  durationRange,
}: {
  side: "a" | "b";
  group: ProcessComparison["a"];
  durationRange?: [number | null, number | null];
}) {
  const { t } = useTranslation();
  const identity = useProcessSourceIdentity(group.process);
  const minutes = (seconds: number) => t("processPage.durationMinutes", { count: seconds / 60 });
  return (
    <section className="min-w-0">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-white"
          style={{ backgroundColor: compareSideColor(side) }}
        >
          {side.toUpperCase()}
        </span>
        {group.label}
        {durationRange && (
          <span className="font-normal text-text-dim">
            · {t("processPage.durationGroupCount", { count: group.runs.length })}
          </span>
        )}
      </h2>
      {durationRange && (
        <div className="mb-3 rounded-lg bg-muted-surface px-3 py-2">
          <p className="text-xs text-text-dim">{t("processPage.activeTime")}</p>
          <p className="mt-1 text-sm font-medium tabular-nums">
            {durationRange[0] === null
              ? `< ${minutes(durationRange[1]!)}`
              : durationRange[1] === null
                ? `> ${minutes(durationRange[0])}`
                : `${minutes(durationRange[0])} – ${minutes(durationRange[1])}`}
          </p>
        </div>
      )}
      <ProcessComparisonMap group={group} side={side} />
      {group.runs.length === 1 && (
        <div className="mt-2">
          <ProcessRunRow
            run={group.runs[0]}
            label={identity(group.runs[0].source).instanceLabel}
            durationLabel={formatDuration(group.runs[0].source.avgDuration)}
          />
        </div>
      )}
      {group.runs.length > 1 && !durationRange && (
        <p className="mt-2 text-xs text-text-mid">
          {t("processPage.averagePerRun")}:{" "}
          {formatDuration(
            group.runs.reduce((sum, run) => sum + run.source.avgDuration, 0) / group.runs.length,
          )}
        </p>
      )}
    </section>
  );
}
